import { noop } from 'lodash';
import { useWorkspaceNotificationsStatusFragment } from '@frameio/notifications/gql';
import { useToggleWorkspaceNotifications } from '@frameio/notifications/hooks';
import {
  AlertDialog,
  AlertDialogPrimaryAction,
  AlertDialogSecondaryAction,
} from '@frameio/vapor';
import TEXT from './generated/TEXT/WorkspaceNotificationsWarningModal';

export type WorkspaceNotificationsWarningModalProps = {
  workspaceId: string;
  isShowing?: boolean;
  onRequestClose?: () => void;
};
/**
 * A dialog that warns the user that enabling/disabling notifications will affect all projects in the workspace.
 */
export function WorkspaceNotificationsWarningModal({
  workspaceId,
  isShowing = false,
  onRequestClose = noop,
}: WorkspaceNotificationsWarningModalProps) {
  const { toggleWorkspaceNotificationsMutation: toggleProjectNotifications } =
    useToggleWorkspaceNotifications({
      workspaceId,
    });

  const { data: workspaceData } = useWorkspaceNotificationsStatusFragment({
    from: {
      __typename: 'Workspace',
      id: workspaceId ?? '',
    },
  });

  const hasProjectNotificationsEnabled =
    workspaceData.projectNotificationsEnabled;

  const handleConfirmation = () => {
    toggleProjectNotifications();
    onRequestClose();
  };

  return (
    <AlertDialog
      actions={
        <>
          <AlertDialogSecondaryAction
            data-testid="toggle-projects-notifications-cancel-button"
            onPress={onRequestClose}
          >
            {TEXT.cancel}
          </AlertDialogSecondaryAction>
          <AlertDialogPrimaryAction
            data-testid="toggle-projects-notifications-button"
            onPress={handleConfirmation}
          >
            {TEXT.confirm(hasProjectNotificationsEnabled)}
          </AlertDialogPrimaryAction>
        </>
      }
      aria-label={TEXT.title(hasProjectNotificationsEnabled)}
      details={TEXT.details(hasProjectNotificationsEnabled)}
      isShowing={isShowing}
      title={TEXT.title(hasProjectNotificationsEnabled)}
      variant="notice"
    />
  );
}

TEXT.DEFINE({
  /** Secondary action label to dismiss the warning dialog */
  cancel: 'Cancel',
  /** Dialog title for enabling/disabling notifications across projects */
  title: (hasProjectNotificationsEnabled: boolean | null | undefined) =>
    hasProjectNotificationsEnabled
      ? 'Disable Notifications for All Projects?'
      : 'Enable Notifications for All Projects?',
  /** Dialog body text describing the impact on all projects */
  details: (hasProjectNotificationsEnabled: boolean | null | undefined) =>
    hasProjectNotificationsEnabled
      ? 'New projects in this Workspace will also have notifications disabled. You’ll still be notified when you’re @mentioned or assigned.'
      : 'New projects in this Workspace will also have notifications enabled.',
  /** Primary action label to confirm the change */
  confirm: (hasProjectNotificationsEnabled: boolean | null | undefined) =>
    hasProjectNotificationsEnabled ? 'Disable' : 'Enable',
});
